// client/src/coms/views/welcome/welcomeData.js
// Recent destinations for the welcome screen, built only from what this device and the account really
// have: the last server/channel and area Voxemera saved (comsSession), the account's servers and its most
// recent direct-message threads. Nothing is invented: with no history the section simply shows less.
import { getPersistedComsNav, getPersistedLastLocation, setPersistedComsNav } from '../../components/comslayout_parts/comsSession';

const LS_LAST_SERVER_ID = 'lastCommsServerId';
const PENDING_PUBLIC_JOIN_KEY = 'vox_pending_public_join';

export const AREA_LABELS = {
  coms: 'Comms',
  social: 'Direct Messages',
  watch: 'Watch Together',
  canvas: 'Canvas',
  games: 'Activities',
  studio: 'Studio',
  maps: 'Maps',
  marketplace: 'Marketplace',
  chats: 'Chats',
};

export function serverKeyOf(s) {
  if (!s || typeof s !== 'object') return '';
  const v = s.id != null ? s.id : s.public_id != null ? s.public_id : s.server_key != null ? s.server_key : s.key;
  return String(v == null ? '' : v).trim();
}

export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]).join('');
  return (letters || String(name || '?').slice(0, 2) || '?').toUpperCase();
}

function readLastServerId() {
  try {
    return String(window.localStorage.getItem(LS_LAST_SERVER_ID) || '').trim();
  } catch {
    return '';
  }
}

// The server Voxemera would reopen: the last one used if the account is still in it, else the first.
export function pickHomeServer(servers, { lastLocation, lastServerId } = {}) {
  const list = Array.isArray(servers) ? servers : [];
  if (!list.length) return null;
  const wanted = [lastLocation && lastLocation.serverId, lastServerId].map((v) => String(v || '').trim()).filter(Boolean);
  for (const w of wanted) {
    const hit = list.find((s) => serverKeyOf(s) === w);
    if (hit) return hit;
  }
  return list[0];
}

// { resume: {serverKey, serverName, channelName, logo} | null, lastArea: 'studio' | ... | null, recentDms: [...] }
// clearedAt (ms): the visitor pressed Clear on this device; activity up to then is not shown again.
export function buildRecent({ servers, lastLocation, comsNav, dmThreads, lastServerId, clearedAt = 0 }) {
  const list = Array.isArray(servers) ? servers : [];
  let resume = null;
  const fresh = (ts) => !clearedAt || (Number(ts) || Date.parse(ts || '') || 0) > clearedAt;
  if (lastLocation && lastLocation.serverId && fresh(lastLocation.ts)) {
    const srv = list.find((s) => serverKeyOf(s) === String(lastLocation.serverId));
    if (srv) {
      const cid = String(lastLocation.channelId || '');
      const ch = (Array.isArray(srv.channels) ? srv.channels : []).find((c) => String(c.id) === cid || String(c.db_id) === cid);
      resume = {
        serverKey: serverKeyOf(srv),
        serverName: String(srv.name || ''),
        channelName: ch && ch.name ? String(ch.name) : '',
        channelKind: ch && ch.kind ? String(ch.kind) : '',
        logo: String(srv.logo_url || ''),
      };
    }
  }
  if (!resume && lastServerId && !clearedAt) {
    const srv = list.find((s) => serverKeyOf(s) === String(lastServerId));
    if (srv) resume = { serverKey: serverKeyOf(srv), serverName: String(srv.name || ''), channelName: '', channelKind: '', logo: String(srv.logo_url || '') };
  }

  const tab = comsNav && comsNav.activeTab ? String(comsNav.activeTab) : '';
  const lastArea = tab && tab !== 'coms' && tab !== 'chats' && AREA_LABELS[tab] && fresh(comsNav && comsNav.ts) ? tab : null;

  const threads = Array.isArray(dmThreads) ? dmThreads : [];
  const active = threads.filter((t) => t && t.id != null && t.last_message_at && fresh(t.last_message_at));
  const recentDms = active
    .slice(0, 4)
    .map((t) => {
      const other = t.other_user || {};
      return {
        id: String(t.id),
        name: String(other.display_name || other.username || `User ${t.other_user_id || ''}`).trim(),
        avatar: String(other.avatar_url || other.avatar || ''),
        unread: !!t.has_unread,
        at: t.last_message_at,
      };
    });

  return { resume, lastArea, recentDms, dmTotal: active.length };
}

export function readRecentInputs() {
  return { lastLocation: getPersistedLastLocation(), comsNav: getPersistedComsNav(), lastServerId: readLastServerId() };
}

// Opens an area of the Voxemera shell the same way the shell itself remembers it: the area is saved in the
// session state ComsLayout restores, then the server path is opened.
export function comsPathForArea(serverKey, area, { socialSection, threadId } = {}) {
  const nav = getPersistedComsNav();
  setPersistedComsNav({
    activeTab: area || 'coms',
    socialSection: socialSection || nav.socialSection,
    selectedSocialThreadId: threadId != null ? threadId : nav.selectedSocialThreadId,
  });
  return serverKey ? `/comms/${encodeURIComponent(serverKey)}` : '/comms';
}

export function readPendingPublicJoin() {
  try {
    return String(window.sessionStorage.getItem(PENDING_PUBLIC_JOIN_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function writePendingPublicJoin(key) {
  try {
    if (key) window.sessionStorage.setItem(PENDING_PUBLIC_JOIN_KEY, String(key));
    else window.sessionStorage.removeItem(PENDING_PUBLIC_JOIN_KEY);
  } catch {}
}

export function formatAgo(iso, now = Date.now()) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(t).toLocaleDateString();
}
